/**
 * Gallery Renderer
 * Handles rendering the image gallery and related UI components
 */
class GalleryRenderer {
  constructor(options = {}) {
    this.gallery = options.gallery;
    this.galleryHeader = options.galleryHeader;
    this.isMobile = window.innerWidth < 768;
    this.placeholderId = null;
    this.selectedImage = null;
    this.loadMoreCallback = options.loadMoreCallback || null;
    this.hasMoreImages = false;
    this.renderedImageIds = new Set(); // Track rendered image IDs
    
    // Listen for resize events to update mobile status
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 768;
    });
    
    console.log('GalleryRenderer initialized');
  }
  
  /**
   * Set the gallery element
   * @param {HTMLElement} gallery 
   */
  setGalleryElement(gallery) {
    this.gallery = gallery;
  }
  
  /**
   * Set the gallery header element
   * @param {HTMLElement} header 
   */
  setGalleryHeaderElement(header) {
    this.galleryHeader = header;
  }
  
  /**
   * Set the selected image
   * @param {Object} image 
   */
  setSelectedImage(image) {
    this.selectedImage = image;
  }
  
  /**
   * Set the callback for loading more images
   * @param {Function} callback 
   */
  setLoadMoreCallback(callback) {
    this.loadMoreCallback = callback;
  }
  
  /**
   * Set whether there are more images to load
   * @param {boolean} hasMore 
   */
  setHasMoreImages(hasMore) {
    this.hasMoreImages = hasMore;
    
    // Update sentinel immediately when this value changes
    if (this.gallery) {
      this.updateInfiniteScrollSentinel();
    }
  }
  
  /**
   * Add a placeholder to the gallery (loading state)
   * @param {string|null} uploadedImageBase64 - Optional base64 image to show with blur
   * @returns {string} ID of the created placeholder
   */
  addPlaceholderToGallery(uploadedImageBase64 = null, selectedColorCard = null, productType = null) {
    if (!this.gallery) return null;
    
    // Generate a unique ID for this placeholder
    this.placeholderId = Date.now();
    
    // Always show gallery header when adding placeholder (image generation starting)
    if (this.galleryHeader) {
      this.galleryHeader.classList.remove('hidden');
    }
    
    // Create placeholder element with gradient border
    const placeholder = document.createElement('div');
    placeholder.className = 'letz-ai__item';
    placeholder.id = `placeholder-${this.placeholderId}`;
    
    // Create inner container
    const innerContainer = document.createElement('div');
    innerContainer.className = 'letz-ai__item-inner';
    
    // Add inner div with gradient background
    const placeholderInner = document.createElement('div');
    
    // Create fallback background when no uploaded image
    let fallbackBackground = '';
    if (!uploadedImageBase64 && selectedColorCard) {
      if (productType === 'color_card' && selectedColorCard.colors) {
        // For color cards, create CSS gradient from the colors
        const colors = selectedColorCard.colors;
        if (colors.length === 1) {
          fallbackBackground = `<div class="letz-ai__placeholder-fallback" style="background-color: ${colors[0]};"></div>`;
        } else if (colors.length > 1) {
          const gradient = `linear-gradient(135deg, ${colors.join(', ')})`;
          fallbackBackground = `<div class="letz-ai__placeholder-fallback" style="background: ${gradient};"></div>`;
        }
      } else if (selectedColorCard.image) {
        // For image cards, use the selected combination image
        fallbackBackground = `<div class="letz-ai__placeholder-fallback" style="background-image: url('${selectedColorCard.image}'); background-size: cover; background-position: center;"></div>`;
      }
    }
    
    // Apply image with blur if provided
    if (uploadedImageBase64) {
      console.log('[SCANNER DEBUG] Creating scanner with base64:', uploadedImageBase64 ? 'EXISTS' : 'NULL');
      placeholderInner.className = 'letz-ai__placeholder with-image';
      
      // Create scanframe container
      const scanFrame = document.createElement('div');
      scanFrame.className = 'scanframe';
      console.log('[SCANNER DEBUG] Created scanframe element');
      
      // Create real img element
      const img = document.createElement('img');
      img.src = uploadedImageBase64;
      img.alt = 'Imagem enviada';
      console.log('[SCANNER DEBUG] Created img element with src');
      
      // Add sweep effect
      const sweep = document.createElement('div');
      sweep.className = 'sweep';
      console.log('[SCANNER DEBUG] Created sweep element');
      
      // Add loading dots overlay
      const loadingDots = document.createElement('div');
      loadingDots.className = 'letz-ai__placeholder-loading';
      loadingDots.innerHTML = '<div class="dot"></div><div class="dot"></div><div class="dot"></div>';
      
      // Assemble structure: scanframe > img + sweep + loading dots
      scanFrame.appendChild(img);
      scanFrame.appendChild(sweep);
      scanFrame.appendChild(loadingDots);
      placeholderInner.appendChild(scanFrame);
      
      console.log('[SCANNER DEBUG] Scanner structure assembled:', {
        scanFrame: scanFrame.outerHTML.substring(0, 100) + '...',
        hasSweepElement: scanFrame.querySelector('.sweep') !== null
      });
    } else {
      // No uploaded image - create scanframe with fallback background
      console.log('[SCANNER DEBUG] Creating scanner without base64 image');
      placeholderInner.className = 'letz-ai__placeholder';
      
      // Create scanframe container
      const scanFrame = document.createElement('div');
      scanFrame.className = 'scanframe';
      console.log('[SCANNER DEBUG] Created scanframe element for fallback');
      
      // Add fallback background if available
      if (fallbackBackground) {
        // For color cards, fallbackBackground is CSS, for image cards it's an image URL
        if (fallbackBackground.startsWith('url(') || fallbackBackground.startsWith('linear-gradient') || fallbackBackground.startsWith('background:')) {
          // Apply CSS background directly
          scanFrame.style.cssText += fallbackBackground;
        } else {
          // Apply as background image
          scanFrame.style.backgroundImage = `url(${fallbackBackground})`;
          scanFrame.style.backgroundSize = 'cover';
          scanFrame.style.backgroundPosition = 'center';
          scanFrame.style.backgroundRepeat = 'no-repeat';
        }
        scanFrame.style.zIndex = '1'; // Behind blur and sweep
      }
      
      // Add sweep effect
      const sweep = document.createElement('div');
      sweep.className = 'sweep';
      console.log('[SCANNER DEBUG] Created sweep element for fallback');
      
      // Add loading dots overlay
      const loadingDots = document.createElement('div');
      loadingDots.className = 'letz-ai__placeholder-loading';
      loadingDots.innerHTML = '<div class="dot"></div><div class="dot"></div><div class="dot"></div>';
      
      // Assemble structure: scanframe > fallback + sweep + loading dots
      scanFrame.appendChild(sweep);
      scanFrame.appendChild(loadingDots);
      placeholderInner.appendChild(scanFrame);
      
      console.log('[SCANNER DEBUG] Scanner structure assembled for fallback');
    }
    innerContainer.appendChild(placeholderInner);
    placeholder.appendChild(innerContainer);
    
    // Ensure correct positioning - add to the beginning of gallery
    const existingImages = this.gallery.querySelectorAll('.letz-ai__item:not(.infinite-scroll-sentinel)');
    if (existingImages.length === 0) {
      // Gallery is empty, add as first item
      this.gallery.appendChild(placeholder);
      console.log('[SCANNER DEBUG] Added placeholder to empty gallery');
    } else {
      // Gallery has items, insert at the beginning
      this.gallery.insertBefore(placeholder, this.gallery.firstChild);
      console.log('[SCANNER DEBUG] Added placeholder at beginning of existing gallery');
    }
    
    // Auto-scroll to the first card (where placeholder was added)
    this.gallery.scrollLeft = 0;
    
    console.log('Placeholder added to gallery with ID:', this.placeholderId);
    
    return this.placeholderId;
  }
  
  /**
   * Remove the placeholder from the gallery
   */
  removePlaceholder() {
    if (this.placeholderId !== null) {
      const placeholder = document.getElementById(`placeholder-${this.placeholderId}`);
      if (placeholder) {
        placeholder.remove();
      }
      this.placeholderId = null;
    }
  }
  
  /**
   * Create sentinel element for infinite scroll
   * @returns {HTMLElement} The created sentinel element
   */
  createInfiniteScrollSentinel() {
    // Check if sentinel already exists
    const existingSentinel = document.getElementById('infinite-scroll-sentinel');
    if (existingSentinel) {
      // Return existing sentinel instead of creating a new one
      return existingSentinel;
    }
    
    // Create new sentinel element with improved visibility
    const sentinel = document.createElement('div');
    sentinel.className = 'letz-ai__sentinel';
    sentinel.id = 'infinite-scroll-sentinel';
    sentinel.style.height = '5px'; // Reduced height for more precise detection
    sentinel.style.width = '100%';
    sentinel.style.marginTop = '10px'; // Reduced margin
    sentinel.style.marginBottom = '10px'; // Reduced margin
    sentinel.style.opacity = '0'; // Hide visually but keep detectable
    sentinel.style.pointerEvents = 'none';
    sentinel.style.position = 'relative';
    sentinel.style.backgroundColor = 'transparent';
    sentinel.setAttribute('data-scroll-sentinel', 'true');
    
    console.log('Created new infinite scroll sentinel with precise positioning');
    return sentinel;
  }
  
  /**
   * Update the infinite scroll sentinel - separate method for better control
   */
  updateInfiniteScrollSentinel() {
    // First, remove any existing sentinel to avoid duplication
    const existingSentinel = document.getElementById('infinite-scroll-sentinel');
    if (existingSentinel) {
      existingSentinel.remove();
    }
    
    // Only add sentinel if there are more images and a callback exists
    if (this.hasMoreImages && this.loadMoreCallback) {
      // Create sentinel element
      const sentinel = this.createInfiniteScrollSentinel();
      
      // Position sentinel differently based on device type 
      if (this.isMobile) {
        // For mobile, use much more conservative positioning to prevent accidental triggers
        sentinel.style.position = 'absolute';
        sentinel.style.right = '10px'; // Position much closer to the right edge (was 200px)
        sentinel.style.bottom = '0';  
        sentinel.style.height = '50%'; // Reduced height for higher intentionality (was 100%)
        sentinel.style.width = '2px'; // Thinner width to be less sensitive
      }
      
      // Append sentinel to the gallery as the very last element
      this.gallery.appendChild(sentinel);
      console.log('Placed infinite scroll sentinel with strict mobile positioning');
      
      // Make sentinel visible
      sentinel.style.display = 'block';
    } else {
      console.log('No more images to load, sentinel not added');
    }
  }
  
  /**
   * Update selected state for an image in the gallery
   * FIXED: Apply selection to the image container instead of just the image
   * @param {string} imageUrl URL of the selected image 
   */
  updateSelectedState(imageUrl) {
    if (!this.gallery) return;
    
    // Always remove 'selected' class from all CONTAINERS first
    const allSelectedContainers = document.querySelectorAll('.letz-ai__item.selected');
    allSelectedContainers.forEach(container => {
      container.classList.remove('selected');
    });
    
    // Find the container of the clicked image and add 'selected' class
    const clickedImage = document.querySelector(`.letz-ai__image[src="${imageUrl}"]`);
    if (clickedImage) {
      const container = clickedImage.closest('.letz-ai__item');
      if (container) {
        container.classList.add('selected');
        console.log('Applied selected class to image container for:', imageUrl);
      }
    }
  }
  
  /**
   * Replace the current placeholder with a generated image
   * FIXED: Remove selection from all containers before setting new one
   * @param {Object} imageData The generated image data
   */
  replacePlaceholderWithImage(imageData) {
    console.log('🔄 REPLACE PLACEHOLDER START');
    console.log('📝 Placeholder ID:', this.placeholderId);
    console.log('📊 Gallery children count before:', this.gallery?.children.length || 0);
    console.log('📊 Gallery HTML before replace (first 300 chars):', this.gallery?.innerHTML.substring(0, 300) + '...');
    
    if (this.placeholderId === null) {
      console.warn('No placeholder to replace');
      return false;
    }
    
    const placeholder = document.getElementById(`placeholder-${this.placeholderId}`);
    console.log('🎯 Placeholder element found:', !!placeholder);
    console.log('📍 Placeholder position in gallery:', placeholder ? Array.from(this.gallery.children).indexOf(placeholder) : -1);
    
    if (!placeholder) {
      console.warn('Placeholder element not found in DOM');
      return false;
    }
    
    console.log('🔄 Replacing placeholder with new image:', imageData);
    
    // FIXED: Remove selected class from all CONTAINERS first
    const allSelectedContainers = document.querySelectorAll('.letz-ai__item.selected');
    allSelectedContainers.forEach(container => {
      container.classList.remove('selected');
    });
    
    // Determine image properties
    const isObject = typeof imageData !== 'string';
    const imageId = isObject ? imageData.id : `img-${Date.now()}`;
    const imageUrl = isObject ? imageData.image_url : imageData;
    const isFeatured = isObject && imageData.is_featured === true;
    const prompt = isObject ? imageData.prompt || '' : '';
    
    // Add to set of rendered images
    this.renderedImageIds.add(isObject ? imageId : imageUrl);
    console.log(`Tracking rendered image ID: ${isObject ? imageId : imageUrl}`);
    
    // New generated images are ALWAYS selected
    const isSelected = true;
    
    // Create new image element HTML
    const mobileClass = this.isMobile ? 'mobile-view' : '';
    const imageDataAttr = isObject ? 
      `data-image-id="${imageId}"` : 
      `data-image-url="${encodeURIComponent(imageUrl)}"`;
    const promptAttr = prompt ? `data-prompt="${prompt.replace(/"/g, '&quot;')}"` : '';
    
    const imageClickHandler = isObject ?
      `onclick="event.preventDefault(); window.imageGenerator.handleImageClick(${JSON.stringify(imageData).replace(/"/g, '&quot;')})"`
      :
      `onclick="event.preventDefault(); window.imageGenerator.handleImageClick('${imageUrl}')"`;
    
    // Featured icon HTML
    const featuredIconHtml = isFeatured ? 
      `<div class="letz-ai__featured-icon" title="Imagem em destaque">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" stroke="currentColor" stroke-width="0.5" style="fill: url(#featured-icon-gradient-new)">
          <defs>
            <linearGradient id="featured-icon-gradient-new" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stop-color="#0894FF" />
              <stop offset="34%" stop-color="#C959DD" />
              <stop offset="68%" stop-color="#FF2E54" />
              <stop offset="100%" stop-color="#FF9004" />
            </linearGradient>
          </defs>
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
        </svg>
       </div>` 
      : '';
      
    // Clipboard icon HTML
    const clipboardIconHtml = prompt ? 
      `<div class="letz-ai__clipboard-icon" 
         title="Copiar prompt para área de transferência" 
         onclick="event.stopPropagation(); window.imageGenerator.copyPromptToClipboard('${prompt.replace(/'/g, "\\'")}')"
       >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A9A9A9" stroke-width="1.5" style="overflow:visible;">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1"></path>
        </svg>
       </div>` 
      : '';
    
    // Delete button HTML - only for non-featured images
    const deleteButtonHtml = !isFeatured ? 
      `<button 
        class="letz-ai__remove" 
        onclick="event.stopPropagation(); window.imageGenerator.removeFromGallery(${isObject ? 
          JSON.stringify(imageData).replace(/"/g, '&quot;') : 
          `'${imageUrl}'`}, this)"
        aria-label="Remover imagem"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M3 6h18m-2 0v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6m3 0V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
        </svg>
      </button>` 
      : '';
    
    // Create temporary image to ensure it loads before replacing
    const tempImg = new Image();
    tempImg.onload = () => {
      // FIXED: Create new image element and replace placeholder completely
      const newImageElement = document.createElement('div');
      newImageElement.className = `letz-ai__item ${mobileClass} ${isFeatured ? 'featured' : ''} selected`;
      newImageElement.setAttribute('data-image-id', imageId);
      if (promptAttr) newImageElement.setAttribute('data-prompt', prompt);
      
      newImageElement.innerHTML = `
        <div class="letz-ai__item-inner">
          <img 
            src="${imageUrl}" 
            alt="Imagem ${isFeatured ? 'em destaque' : 'gerada'}" 
            class="letz-ai__image"
            ${imageClickHandler}
          >
          ${featuredIconHtml}
          ${clipboardIconHtml}
          ${deleteButtonHtml}
        </div>
      `;
      
      // Replace placeholder with new image element
      console.log('🔧 Executing placeholder.replaceWith()...');
      placeholder.replaceWith(newImageElement);
      console.log('✅ Placeholder replaced with new image element');
      
      // Clear placeholder ID since it's no longer a placeholder
      this.placeholderId = null;
      
      console.log('📊 Gallery children count after replace:', this.gallery.children.length);
      console.log('📊 Gallery HTML after replace (first 300 chars):', this.gallery.innerHTML.substring(0, 300) + '...');
      console.log('🔍 New image element position:', Array.from(this.gallery.children).findIndex(child => child.classList.contains('letz-ai__gallery')));
      console.log('✅ Successfully replaced placeholder with new image and auto-selected container');
    };
    
    tempImg.onerror = () => {
      console.error('Failed to load new image, keeping placeholder');
    };
    
    // Start loading the image
    tempImg.src = imageUrl;
    
    return true;
  }
  
  /**
   * Render the gallery with the provided images
   * @param {Array} images Array of image objects
   * @param {Object} options Rendering options
   * @param {boolean} options.append Whether to append or replace images
   */
  renderGallery(images = [], options = {}) {
    if (!this.gallery) {
      console.error('Gallery element not found for rendering');
      return;
    }
    
    const append = options.append === true;
    console.log(`Rendering gallery, append mode: ${append}, image count: ${images.length}`);
    
    // Check if gallery has maintain-height class
    const hasMaintainHeight = this.gallery.classList.contains('maintain-height');
    
    // If no images and no placeholder, handle clearing carefully
    if ((!images || images.length === 0) && this.placeholderId === null && !append) {
      if (hasMaintainHeight) {
        // When maintain-height is active, preserve a placeholder div to maintain height
        console.log('Gallery has maintain-height, preserving placeholder div for height');
        this.gallery.innerHTML = '<div class="letz-ai__height-placeholder" style="width: 100%; height: 1px; opacity: 0; pointer-events: none;"></div>';
      } else {
        // Normal clearing when maintain-height is not active
        this.gallery.innerHTML = ''; // Clear gallery content
      }
      
      // CRITICAL FIX: Only hide header when NOT maintaining height
      if (this.galleryHeader && !hasMaintainHeight) {
        this.galleryHeader.classList.add('hidden'); // Hide header only when not maintaining height
        console.log('Header hidden because maintain-height is not active');
      } else if (this.galleryHeader && hasMaintainHeight) {
        console.log('Header kept visible because maintain-height is active');
      }
      
      console.log('Gallery cleared with maintain-height consideration, header visibility maintained, tracked IDs cleared, and sentinel updated.');
      this.renderedImageIds.clear(); // Clear tracked IDs for consistency
      this.updateInfiniteScrollSentinel(); // Remove or update sentinel
      
      // Only trigger empty state update if not preserving height
      if (!hasMaintainHeight) {
        setTimeout(() => {
          if (window.imageGenerator && typeof window.imageGenerator.updateGalleryEmptyState === 'function') {
            window.imageGenerator.updateGalleryEmptyState();
          }
        }, 0);
      }
      
      return;
    }

    // Show header since we have content to display or a placeholder
    if (this.galleryHeader) this.galleryHeader.classList.remove('hidden');
    
    let placeholderHtml = '';
    if (this.placeholderId !== null) {
      // Check if placeholder still exists in DOM
      const placeholder = document.getElementById(`placeholder-${this.placeholderId}`);
      if (!placeholder) {
        // If placeholder no longer exists in DOM but we still have its ID, recreate it
        placeholderHtml = `
          <div class="letz-ai__item" id="placeholder-${this.placeholderId}">
            <div class="letz-ai__item-inner">
              <div class="letz-ai__placeholder">
                <div class="letz-ai__placeholder-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="url(#icon-gradient)" stroke-width="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        `;
      }
    }
    
    if (!append) {
      console.log('Reset mode: clearing tracked rendered image IDs');
      this.renderedImageIds.clear();
    } else {
      console.log(`Append mode: currently tracking ${this.renderedImageIds.size} image IDs`);
    }
    
    // Filter out already rendered images if appending
    const newImagesToRender = append ? 
      images.filter(image => {
        const imageId = typeof image === 'string' ? image : image.id;
        const alreadyRendered = this.renderedImageIds.has(imageId);
        if (alreadyRendered) {
          console.log(`Image ${imageId} already rendered, skipping`);
        }
        return !alreadyRendered;
      }) : 
      images;
    
    console.log(`Rendering ${newImagesToRender.length} new images, append: ${append}, tracked image IDs: ${this.renderedImageIds.size}`);
    
    // If no new images to render and not in reset mode, just update the infinite scroll sentinel
    if (newImagesToRender.length === 0 && append) {
      console.log('No new images to append, only updating infinite scroll sentinel');
      this.updateInfiniteScrollSentinel();
      return;
    }
    
    const imagesHtml = newImagesToRender
      .map((image, index) => {
        const isObject = typeof image !== 'string';
        const imageId = isObject ? image.id : `img-${index}`;
        const imageUrl = isObject ? image.image_url : image;
        const isFeatured = isObject && image.is_featured === true;
        const prompt = isObject ? image.prompt || '' : '';
        
        // Add to set of rendered images
        this.renderedImageIds.add(isObject ? imageId : imageUrl);
        console.log(`Tracking rendered image ID: ${isObject ? imageId : imageUrl}`);
        
        let isSelected = false;
        if (this.selectedImage) {
          if (typeof this.selectedImage === 'string') {
            isSelected = this.selectedImage === imageUrl;
          } else if (typeof this.selectedImage === 'object') {
            isSelected = this.selectedImage.id === imageId || 
                        this.selectedImage.image_url === imageUrl;
          }
        }
        
        const mobileClass = this.isMobile ? 'mobile-view' : '';
        const imageDataAttr = isObject ? 
          `data-image-id="${imageId}"` : 
          `data-image-url="${encodeURIComponent(imageUrl)}"`;
        const promptAttr = prompt ? `data-prompt="${prompt.replace(/"/g, '&quot;')}"` : '';
        
        const imageClickHandler = isObject ?
          `onclick="event.preventDefault(); window.imageGenerator.handleImageClick(${JSON.stringify(image).replace(/"/g, '&quot;')})"`
          :
          `onclick="event.preventDefault(); window.imageGenerator.handleImageClick('${imageUrl}')"`;
        
        const gradientColors = {
          stop1: '#0894FF',
          stop2: '#C959DD',
          stop3: '#FF2E54',
          stop4: '#FF9004'
        };
        
        const featuredIconHtml = isFeatured ? 
          `<div class="letz-ai__featured-icon" title="Imagem em destaque">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" stroke="currentColor" stroke-width="0.5" style="fill: url(#featured-icon-gradient-${index})">
              <defs>
                <linearGradient id="featured-icon-gradient-${index}" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="${gradientColors.stop1}" />
                  <stop offset="34%" stop-color="${gradientColors.stop2}" />
                  <stop offset="68%" stop-color="${gradientColors.stop3}" />
                  <stop offset="100%" stop-color="${gradientColors.stop4}" />
                </linearGradient>
              </defs>
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
            </svg>
           </div>` 
          : '';
          
        const clipboardIconHtml = prompt ? 
          `<div class="letz-ai__clipboard-icon" 
             title="Copiar prompt para área de transferência" 
             onclick="event.stopPropagation(); window.imageGenerator.copyPromptToClipboard('${prompt.replace(/'/g, "\\'")}')"
           >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#A9A9A9" stroke-width="1.5" style="overflow:visible;">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1"></path>
            </svg>
           </div>` 
          : '';
        
        const deleteButtonHtml = !isFeatured ? 
          `<button 
            class="letz-ai__remove" 
            onclick="event.stopPropagation(); window.imageGenerator.removeFromGallery(${isObject ? 
              JSON.stringify(image).replace(/"/g, '&quot;') : 
              `'${imageUrl}'`}, this)"
            aria-label="Remover imagem"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 6h18m-2 0v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6m3 0V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
            </svg>
          </button>` 
          : '';
          
        return `
          <div class="letz-ai__item ${mobileClass} ${isFeatured ? 'featured' : ''} ${isSelected ? 'selected' : ''}" ${imageDataAttr} ${promptAttr}>
            <div class="letz-ai__item-inner">
              <img 
                src="${imageUrl}" 
                alt="Imagem ${isFeatured ? 'em destaque' : 'gerada'} ${index + 1}" 
                class="letz-ai__image ${isSelected ? 'selected' : ''}"
                ${imageClickHandler}
              >
              ${featuredIconHtml}
              ${clipboardIconHtml}
              ${deleteButtonHtml}
            </div>
          </div>
        `;
      })
      .join('');
    
    // First, remove any existing sentinel to avoid duplication
    const existingSentinel = document.getElementById('infinite-scroll-sentinel');
    if (existingSentinel) {
      existingSentinel.remove();
    }
    
    // CRITICAL FIX: For first image, ensure correct positioning
    if (append) {
      // Check if this is the first image being added to an empty gallery
      const existingImages = this.gallery.querySelectorAll('.letz-ai__item:not([id^="placeholder"])').length;
      const placeholder = document.getElementById(`placeholder-${this.placeholderId}`);
      
      if (imagesHtml.length > 0) {
        if (placeholder && existingImages === 0) {
          // First image: replace gallery content entirely to ensure correct positioning
          this.gallery.innerHTML = imagesHtml;
          this.placeholderId = null; // Clear placeholder ID
          console.log('First image: Gallery recreated with new content');
        } else if (existingImages === 0 && !placeholder) {
          console.log('🔍 RENDER: First image case - checking for existing positioned image');
          console.log('📊 Gallery HTML before check:', this.gallery.innerHTML.substring(0, 200) + '...');
          
          // Check if first image was already correctly positioned by replacePlaceholderWithImage
          const hasCorrectlyPositionedImage = this.gallery.querySelector('.letz-ai__gallery');
          console.log('🎯 Found correctly positioned image:', !!hasCorrectlyPositionedImage);
          
          if (!hasCorrectlyPositionedImage) {
            console.log('❌ No correctly positioned image found - recreating gallery');
            console.log('📝 New imagesHtml length:', imagesHtml.length);
            this.gallery.innerHTML = imagesHtml;
            console.log('✅ Gallery recreated with new content');
          } else {
            console.log('✅ First image already correctly positioned - appending remaining');
            console.log('📝 Appending imagesHtml length:', imagesHtml.length);
            this.gallery.insertAdjacentHTML('beforeend', imagesHtml);
            console.log('✅ Images appended to existing correctly positioned image');
          }
          console.log('📊 Gallery HTML after operation:', this.gallery.innerHTML.substring(0, 200) + '...');
        } else if (placeholder) {
          // Subsequent images: insert before placeholder
          placeholder.insertAdjacentHTML('beforebegin', imagesHtml);
          console.log('New images inserted before placeholder');
        } else {
          // No placeholder, append at the end
          this.gallery.insertAdjacentHTML('beforeend', imagesHtml);
          console.log('New images appended to gallery');
        }
      }
    } else {
      // For reset mode, replace the entire content
      console.log('Reset mode: replacing entire gallery content');
      this.gallery.innerHTML = placeholderHtml + imagesHtml;
    }
    
    // Update sentinel element for infinite scroll after rendering images
    setTimeout(() => {
      this.updateInfiniteScrollSentinel();
      
      // Only trigger empty state update if not preserving height
      if (!hasMaintainHeight && window.imageGenerator && typeof window.imageGenerator.updateGalleryEmptyState === 'function') {
        window.imageGenerator.updateGalleryEmptyState();
      }
    }, 0);
    
    console.log(`Gallery rendered with ${newImagesToRender.length} new images, total tracked: ${this.renderedImageIds.size}`);
  }
}

// Export the class
window.GalleryRenderer = GalleryRenderer;
